"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Home,
} from "lucide-react";
import { BookingStep } from "@/types/booking";
import { formatDuration, formatPrice } from "@/lib/utils";
import { useScrollToTop } from "@/hooks/useScrollToTop";

export default function Success() {
  useScrollToTop();

  const {
    selectedServices,
    appointmentDateTime,
    businessInfo,
    selectedSalon,
    serviceClient,
    bookedAppointment,
    handleNextStep,
    resetBooking,
    priceSummary,
    appointmentPaymentType,
    actualAmountPaid,
    actualProcessingFee,
  } = useBooking();

  // Format appointment date
  const formatAppointmentDate = () => {
    if (!appointmentDateTime) return "";
    const date = new Date(appointmentDateTime);
    if (isNaN(date.getTime())) return "Date unavailable";
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Format appointment time
  const formatAppointmentTime = () => {
    if (!appointmentDateTime) return "";
    const date = new Date(appointmentDateTime);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Calculate total duration
  const totalDuration = selectedServices.reduce(
    (sum, service) => sum + (service.duration || 0),
    0
  );
  const totalPrice = selectedServices.reduce((sum, service) => {
    const price = service.staffPrice || service.price || 0;
    const quantity = service.quantity || 1;
    return sum + price * quantity;
  }, 0);

  const handleNewBooking = () => {
    resetBooking();
    handleNextStep(BookingStep.LANDING);
  };

  return (
    <div className="min-h-screen bg-grey-5">
      {/* Success Header */}
      <div className="bg-green-50 border-b border-green-200">
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-green-300/10 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-grey-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-grey-600">
            Your appointment has been successfully booked
          </p>
          {bookedAppointment?.bookingId && (
            <p className="text-sm text-grey-500 mt-2">
              Booking ID: {bookedAppointment.bookingId}
            </p>
          )}
        </div>
      </div>

      {/* Booking Details */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Appointment Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Appointment Details
          </h2>

          <div className="space-y-4">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">
                  {formatAppointmentDate()}
                </p>
                <p className="text-sm text-grey-600">
                  {formatAppointmentTime()}
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">
                  {businessInfo?.name}
                </p>
                <p className="text-sm text-grey-600">
                  {selectedSalon?.address}
                  {selectedSalon?.city && `, ${selectedSalon.city}`}
                  {selectedSalon?.state && `, ${selectedSalon.state}`}
                </p>
                {selectedSalon?.phone && (
                  <p className="text-sm text-grey-600">
                    Tel: {selectedSalon.callingCode || "+234"}{" "}
                    {selectedSalon.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="font-medium text-grey-900">Duration</p>
                <p className="text-sm text-grey-600">
                  {formatDuration(totalDuration)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Services Booked
          </h2>

          <div className="space-y-3">
            {selectedServices.map((service) => (
              <div
                key={service.id}
                className="flex justify-between items-start pb-3 border-b border-grey-100 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-grey-900">{service.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {service.staff && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-grey-400" />
                        <span className="text-sm text-grey-600">
                          {service.staff.user?.firstName}{" "}
                          {service.staff.user?.lastName}
                        </span>
                      </div>
                    )}
                    <span className="text-sm text-grey-600">
                      {formatDuration(service.duration)}
                    </span>
                  </div>
                </div>
                <p className="font-medium text-grey-900">
                  {formatPrice(
                    (service.staffPrice || service.price) *
                      (service.quantity || 1),
                    businessInfo?.country?.currency[0]?.symbol || "₦"
                  )}
                </p>
              </div>
            ))}

            {/* Processing Fee - only shown when a fee was actually charged */}
            {actualProcessingFee && actualProcessingFee > 0 && appointmentPaymentType !== "unpaid" && (
              <div className="flex justify-between items-center pt-3 border-t border-grey-100">
                <span className="text-sm text-grey-600">
                  Subtotal
                </span>
                <span className="text-sm text-grey-900">
                  {formatPrice(
                    appointmentPaymentType === "deposit_payment" && priceSummary?.depositAmount
                      ? priceSummary.depositAmount
                      : priceSummary?.totalPaid || totalPrice,
                    businessInfo?.country?.currency[0]?.symbol || "₦"
                  )}
                </span>
              </div>
            )}
            {actualProcessingFee && actualProcessingFee > 0 && appointmentPaymentType !== "unpaid" && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-grey-600">
                  Processing fee
                </span>
                <span className="text-sm text-grey-900">
                  {formatPrice(
                    actualProcessingFee,
                    businessInfo?.country?.currency[0]?.symbol || "₦"
                  )}
                </span>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center pt-3">
              <span className="text-lg font-semibold text-grey-900">
                {appointmentPaymentType === "unpaid" ? "Amount Due" : "Total Paid"}
              </span>
              <span className={`text-xl font-bold ${appointmentPaymentType === "unpaid" ? "text-grey-900" : "text-green-600"}`}>
                {formatPrice(
                  appointmentPaymentType === "unpaid"
                    ? totalPrice
                    : actualAmountPaid
                      ? actualAmountPaid
                      : appointmentPaymentType === "deposit_payment" && priceSummary?.depositAmount
                        ? priceSummary.depositAmount
                        : priceSummary?.totalPaid || totalPrice,
                  businessInfo?.country?.currency[0]?.symbol || "₦"
                )}
              </span>
            </div>
            {appointmentPaymentType === "unpaid" && (
              <p className="text-sm text-grey-600 mt-2">
                Payment will be collected at the venue
              </p>
            )}
          </div>
        </div>

        {/* Customer Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-grey-100 p-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Customer Information
          </h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Name</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.firstName} {serviceClient?.lastName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Email</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-grey-400" />
              <div>
                <p className="text-sm text-grey-600">Phone</p>
                <p className="font-medium text-grey-900">
                  {serviceClient?.callingCode} {serviceClient?.phone}
                </p>
              </div>
            </div>

            {serviceClient?.address && (
              <div className="flex items-center gap-3">
                <Home className="h-5 w-5 text-grey-400" />
                <div>
                  <p className="text-sm text-grey-600">Address</p>
                  <p className="font-medium text-grey-900">
                    {serviceClient.address}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleNewBooking}
            className="flex-1 bg-green-300 hover:bg-green-400 text-white"
          >
            Book Another Appointment
          </Button>
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex-1 border-grey-300"
          >
            Print Confirmation
          </Button>
        </div>

        {/* Add to Calendar */}
        <div className="text-center">
          <p className="text-sm text-grey-600 mb-2">Add to your calendar</p>
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!appointmentDateTime) return;
                // Google Calendar link
                const startDate = new Date(appointmentDateTime)
                  .toISOString()
                  .replace(/-|:|\.\d\d\d/g, "");
                const endDate = new Date(
                  new Date(appointmentDateTime).getTime() +
                    totalDuration * 60000
                )
                  .toISOString()
                  .replace(/-|:|\.\d\d\d/g, "");
                const details = selectedServices.map((s) => s.name).join(", ");
                const location = `${selectedSalon?.address}, ${selectedSalon?.city}, ${selectedSalon?.state}`;

                window.open(
                  `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
                    `Appointment at ${businessInfo?.name}`
                  )}&dates=${startDate}/${endDate}&details=${encodeURIComponent(
                    details
                  )}&location=${encodeURIComponent(location)}`,
                  "_blank"
                );
              }}
            >
              Google Calendar
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!appointmentDateTime) return;
                // Download ICS file
                const startDate = new Date(appointmentDateTime);
                const endDate = new Date(
                  startDate.getTime() + totalDuration * 60000
                );
                const details = selectedServices.map((s) => s.name).join(", ");
                const location = `${selectedSalon?.address}, ${selectedSalon?.city}, ${selectedSalon?.state}`;

                const icsContent = `BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//Your Company//Your App//EN\r\nBEGIN:VEVENT\r\nDTSTART:${startDate
                  .toISOString()
                  .replace(/-|:|\.\d\d\d/g, "")}\r\nDTEND:${endDate
                  .toISOString()
                  .replace(/-|:|\.\d\d\d/g, "")}\r\nSUMMARY:Appointment at ${
                  businessInfo?.name
                }\r\nDESCRIPTION:${details}\r\nLOCATION:${location}\r\nEND:VEVENT\r\nEND:VCALENDAR`;

                // Safari-compatible download method
                const isSafari = /^((?!chrome|android).)*safari/i.test(
                  navigator.userAgent
                );

                if (isSafari) {
                  // For Safari, use data URI approach
                  const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(
                    icsContent
                  )}`;
                  const a = document.createElement("a");
                  a.href = dataUri;
                  a.download = "appointment.ics";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                } else {
                  // For other browsers, use blob URL
                  const blob = new Blob([icsContent], {
                    type: "text/calendar;charset=utf-8",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "appointment.ics";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }
              }}
            >
              Download .ics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
